import { AppointmentPhase, Status } from "components";
import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate";
import WebsiteLink from "components/Website/WebsiteLink";
import React from "react";
import ShowDateTime from "show/components/ShowDateTime";
import { useVisibleFreefieldCategoriesWithValues } from "shared/utils/freeFields/hooks";
import { freeFieldsForWebformSections } from "show/components/FreeFieldsForWebform";
import { checkPromoterCompanySection, checkVenueCompanySection } from "./checkCompanySection";
import { checkPromoterContactSection, checkVenueContactSection } from "./checkContactSection";
import { isFieldHidden } from "./utils";
import { routes } from "utilities/routing/generatedRoutes";
import NotValidatedRow from "components/DetailPage/NotValidatedRow";
import moment from "moment";
import { useContact } from "utilities/api/contactQuery";

interface FromWebformSectionProps {
  venue?: server.dto.Venue
  venueContact?: server.dto.Contact
  similarVenueCompanyCount: number
  similarVenueContactCount: number
  promoterCompany?: server.dto.Company
  promoterContact?: server.dto.Contact
  similarPromoterCompanyCount: number
  similarPromoterContactCount: number
  productsByParent: server.dto.ProductsByParent
  show: server.dto.Show
  webform: server.dto.Webform
  freeFieldCategories: server.dto.FreeFieldCategorySmall[]
}

export const FromWebformSection = (props: FromWebformSectionProps) => {
  const tickets = props.show.tickets.tickets;
  const presaleTicket = tickets?.find(t => t.type.value === "presale");
  const freefieldCategoriesWithValues = useVisibleFreefieldCategoriesWithValues(props.freeFieldCategories, props.show.freeFields);

  const approvelCardSubLabel = props.show.validationStatus === "notValidatedInputByExternal"
    ? `${RESX.Planning.InsertedBy} ${props.show.createdUser.fullName} ${moment.utc(props.show.createdUtc).fromNow()}`
    : `${RESX.Planning.InputFromWebform}`;

  const venueContact = useContact(props.venueContact);
  const promoterContact = useContact(props.promoterContact);

  return <DetailSectionTemplate
    onValueChanged={() => { }}
    onSuccessfulUpdate={() => null}
    isInPageEditable={true}
    sections={[{
      key: "mainSection",
      type: "regular",
      title: RESX.Webform.ValidationOfInputThroughWebform,
      fields: [{
        key: "externalInput",
        label: RESX.Planning.ExternalInput,
        render: () => <NotValidatedRow
          canApprove={props.show.permissions.canApprove}
          sublabel={approvelCardSubLabel}
          approveEndpoint={() => window.CoreApi.Show.Approve({
            showId: props.show.showId,
          })} />
        ,
      }, {
        key: "webformTitle",
        label: RESX.Planning.InputFromWebform,
        render: () => <a href={routes.webform_Detail(props.webform.webformId).absolute}>{props.webform.title}</a>
      }, {
        key: "show",
        label: site.currentEnvironment.show.singular,
        hidden: isFieldHidden(props.webform.eventName),
        render: () => <span>{props.show.title}</span>,
      }, {
        key: "date",
        label: RESX.DateTime.lblDate,
        render: () => <ShowDateTime dateTime={props.show.dateTime} />,
      }, {
        key: "status",
        label: RESX.Status.resxStatus,
        render: () => <div className="flex gap-small">
          <AppointmentPhase value={props.show.status.appointmentPhase} displayLabel={false} />
          <Status status={props.show.status} />
        </div>
      }, {
        key: "eventWebsite",
        label: RESX.Show.EventWebsite,
        isEmpty: !props.show.eventWebsite,
        hidden: isFieldHidden(props.webform.eventWebsite),
        render: () => <WebsiteLink website={props.show.eventWebsite} />,
      }, {
        key: "tickets",
        label: RESX.Ticket.Tickets,
        isEmpty: !presaleTicket,
        hidden: isFieldHidden(props.webform.ticketPrice),
        render: () => <span>{presaleTicket.formatted}</span>,
      }, {
        key: "financialOffer",
        label: RESX.Financial.FinancialOffer,
        isEmpty: !props.productsByParent,
        hidden: isFieldHidden(props.webform.financialOffer),
        render: () => <span>{props.productsByParent.total.sales.formatted}</span>,
      }]
    },
    ...freeFieldsForWebformSections(freefieldCategoriesWithValues, props.webform.freeFields),
    checkVenueCompanySection(
      props.venue.company,
      props.venueContact,
      props.venue,
      props.webform.venue.company,
      props.webform.venue.venue,
      props.show,
      props.similarVenueCompanyCount,
    ),
    checkVenueContactSection(
      venueContact,
      props.webform.venue.contact,
      props.show,
      props.similarVenueContactCount
    ),
    checkPromoterCompanySection(
      props.promoterCompany,
      props.promoterContact,
      props.webform.promoter.company,
      props.show,
      props.similarPromoterCompanyCount
    ),
    checkPromoterContactSection(
      promoterContact,
      props.webform.promoter.contact,
      props.show,
      props.similarPromoterContactCount
    )]}
  />
}